import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCabinet } from '../App';
import { Config, WebUrls as urls } from '../utills/config';
import { getApiRequest, postApiRequest } from '../utills/requests';
import { Button, connectionsFor, dateLabel, DetailRow, Empty, Icon, money, Notice, PageHeading, speedLabel, trafficLabel } from './ui';
function ConnectionCard({ connection, point }) {
    const { account, address, id } = connection;
    const paused = !!account.disable;
    return <Link
        className="connection-card"
        to={`${urls.AccountPage}?pin=${encodeURIComponent(point.pin)}&connection=${encodeURIComponent(id)}`}
    >
        <div className="connection-top">
            <span className="icon-tile blue">
                <Icon name={account.speed_lim ? 'wifi' : 'globe'} />
            </span>
            <span className={`badge ${paused ? 'paused' : ''}`}>
                <span className="status-dot" />
                {paused ? 'Неактивно' : 'Активно'}
            </span>
        </div>
        <p className="connection-address">{address}</p>
        <h3>{account.tarif?.name || 'Подключение CLN'}</h3>
        <div className="connection-metrics">
            <span>
                {account.speed_lim || account.speed_lim_actual ? <>
                    <Icon name="speed" size={16} />
                    {speedLabel(account.speed_lim_actual || account.speed_lim)}
                </> : 'Услуга CLN'}
            </span>
            <strong>
                {money(account.tarif?.abon)} <small>₽ / мес.</small>
            </strong>
        </div>
        <div className="connection-link">Подробнее о подключении <Icon name="arrow" size={17} /></div>
    </Link>;
}
function ConnectionsList({ point }) {
    const connections = connectionsFor(point);
    return connections.length ? <div className="connection-grid">
        {connections.map(connection => <ConnectionCard key={connection.id} connection={connection} point={point} />)}
    </div> : <div className="panel">
        <Empty title="Подключения не найдены">Информация появится здесь после подключения услуг.</Empty>
    </div>;
}
function TransactionList({ items }) {
    return <div className="transaction-list">
        {items.map((item, index) => {
            const positive = Number(item.sum) > 0;
            return <div className="transaction" key={`${item.id}-${index}`}>
                <span className={`transaction-icon ${positive ? 'income' : ''}`}>
                    <Icon name={positive ? 'plus' : 'wifi'} size={20} />
                </span>
                <div className="transaction-description">
                    <strong>{item.comment || 'Операция по счёту'}</strong>
                    <span>
                        {dateLabel(item.date)}
                        {item.pin ? ` · № ${item.pin}` : ''}
                    </span>
                </div>
                <strong className={positive ? 'positive' : ''}>
                    {positive ? '+' : '−'}
                    {money(Math.abs(Number(item.sum)))} <small>₽</small>
                </strong>
            </div>;
        })}
    </div>;
}
function TransactionsPanel({ pin }) {
    const { extras, loadExtras } = useCabinet();
    // The current backend uses `id` for the PIN, not a transaction identifier.
    const items = extras.transactions.filter(item => String(item.pin ?? item.id) === String(pin));
    if (extras.loading)
        return <div className="panel muted" role="status">Загружаем платежи…</div>;
    if (extras.failed.includes('transactions'))
        return <div className="panel">
            <Notice error>История временно недоступна.</Notice>
            <Button variant="secondary" onClick={loadExtras}>Повторить</Button>
        </div>;
    return <>
        <p className="history-note muted">Последние платежи, доступные в приложении. Полная история пока недоступна.</p>
        <div className="panel transactions-panel">
            {items.length ? <TransactionList items={items} /> : <Empty icon="history" title="Нет доступных платежей">В полученных данных нет платежей для этого PIN.</Empty>}
        </div>
    </>;
}
function AccountError({ point }) {
    const { active: selected } = useCabinet();
    const active = point || selected;
    return <div className="panel">
        <Empty icon="shield" title="Нужно войти повторно">Доступ к PIN {active.pin} требует авторизации.</Empty>
        <Link className="button primary" to={`${urls.LoginPage}?pin=${active.pin}`}>Войти в аккаунт</Link>
    </div>;
}
function PinCard({ point, expanded = false }) {
    const connections = connectionsFor(point);
    const inactive = connections.filter(item => item.account.disable).length;
    return <section className="pin-summary" aria-label={`PIN ${point.pin}`}>
        <Link className="pin-summary-main" to={`/v2/pin?pin=${point.pin}`} aria-label={`Открыть PIN ${point.pin}`}>
            <span className="icon-tile blue">
                <Icon name="user" />
            </span>
            <div className="pin-summary-info">
                <h2>PIN {point.pin}</h2>
                <p className="muted">
                    {point.error ? 'Требуется вход' : `Подключений: ${connections.length}`}
                    {!point.error && inactive > 0 ? ` · неактивных: ${inactive}` : ''}
                </p>
            </div>
            <div className={`pin-summary-balance ${Number(point.balance) <= 0 ? 'negative' : ''}`}>
                <span className="muted">Баланс</span>
                <strong>{point.error ? '—' : `${money(point.balance)} ₽`}</strong>
            </div>
            <Icon name="chevron" size={18} />
        </Link>
        {expanded && !point.error && <div className="pin-topup">
            <Link className="button primary full-width" to={`${urls.PaymentPage}?pin=${point.pin}`}><Icon name="plus" size={20} /> Пополнить баланс</Link>
        </div>}
        <div className="pin-summary-actions">
            {point.error
                ? <Link className="text-link" to={`${urls.LoginPage}?pin=${point.pin}`}>
                    Войти с PIN {point.pin}</Link>
                : !expanded &&
                <Link className="text-link" to={`${urls.PaymentPage}?pin=${point.pin}`}>
                    <Icon name="plus" size={16} />
                    Пополнить баланс
                </Link>}
            <Link className="text-link" to={`${urls.Settings}?pin=${point.pin}`}>
                <Icon name="settings" size={16} />
                Настройки</Link>
        </div>
    </section>;
}
export function PinDetails() {
    const { accounts } = useCabinet();
    const location = useLocation();
    const pin = new URLSearchParams(location.search).get('pin');
    const point = accounts.find(item => String(item.pin) === pin);
    if (!point)
        return <>
            <PageHeading title="Аккаунт не найден" />
            <Link className="button primary" to={urls.UserPage}>К аккаунтам</Link>
        </>;
    return <>
        <PageHeading eyebrow="АККАУНТ" title={`PIN ${point.pin}`} />
        <PinCard point={point} expanded />
        {point.error ? <AccountError point={point} /> : <>
            <div className="section-heading">
                <h2>Подключения</h2>
            </div>
            <ConnectionsList point={point} />
            <div className="section-heading">
                <h2>История платежей</h2>
            </div>
            <TransactionsPanel pin={point.pin} />
        </>}
    </>;
}
export function Dashboard() {
    const { accounts, extras, reload } = useCabinet();
    const [refreshing, setRefreshing] = useState(false);
    const refresh = async () => {
        setRefreshing(true);
        try {
            await reload();
        } catch {
        } finally {
            setRefreshing(false);
        }
    };
    return <div className="dashboard-page">
        {extras.stories.length > 0 && <Stories />}
        <div className="section-heading">
            <h2>Аккаунты <span className="count">{accounts.length}</span></h2>
            <div className="account-heading-actions">
                <button
                    className="icon-button"
                    aria-label="Обновить аккаунты"
                    title="Обновить аккаунты"
                    onClick={refresh}
                    disabled={refreshing}
                >
                    <Icon name="history" size={16} />
                </button>
                <Link className="text-link" to={urls.LoginPage}><Icon name="plus" size={18} /> Добавить PIN</Link>
            </div>
        </div>
        <div className="pins-grid">{accounts.map(point => <PinCard key={point.pin} point={point} />)}</div>
        {extras.profile?.welcome_message?.message && <Notice>{extras.profile.welcome_message.message}</Notice>}
        <div className="dashboard-bottom">
            <Link className="dashboard-help" to={urls.Support}>
                <Icon name="help" />
                <span>Нужна помощь?</span>
                <Icon name="arrow" size={18} />
            </Link>
        </div>
    </div>;
}
function Stories() {
    const { extras, isDemo } = useCabinet();
    const [error, setError] = useState('');
    const [viewed, setViewed] = useState([]);
    const [failedImages, setFailedImages] = useState([]);
    const openStory = async (story) => {
        setError('');
        if (isDemo) {
            setViewed(items => [...items, story.id]);
            setError('Это пример карточки новости. В вашем кабинете здесь открывается публикация CLN.');
            return;
        }
        try {
            const url = new URL(story.url);
            if (!['https:', 'http:'].includes(url.protocol))
                throw new Error('Ссылка недоступна.');
            Config.tgWindow.openLink(url.href, { try_instant_view: true });
            const response = await postApiRequest('/stories', { story: story.id });
            if (!response.ok)
                throw new Error('Новость открыта, но не удалось сохранить отметку просмотра.');
            setViewed(items => [...items, story.id]);
        }
        catch (err) {
            setError(err.message);
        }
    };
    return <section className="news-carousel" aria-label="Новости CLN">
        <div className="news-heading">
            <h2>Узнай больше</h2>
        </div>
        <div className="news-track" tabIndex={0} aria-label="Лента новостей">
            {extras.stories.map((story, index) => {
                const seen = story.viewed || viewed.includes(story.id);
                return <button
                    className={`news-card news-tone-${index % 3} ${seen ? 'is-viewed' : 'is-new'}`}
                    key={story.id}
                    aria-label={`${story.name}${seen ? '' : ' — новая история'}`}
                    onClick={() => openStory(story)}
                >
                    {story.image && !failedImages.includes(story.id) ? <img
                        className="news-cover"
                        src={story.image}
                        alt=""
                        loading="lazy"
                        onError={() => setFailedImages(items => [...items, story.id])}
                    /> : <div className="news-art" aria-hidden="true">
                        <Icon name={['wifi', 'shield', 'globe'][index % 3]} size={58} />
                        <i />
                        <i />
                    </div>}
                    <div className="news-shade" />
                    {!seen && <span className="news-new">Новое</span>}
                    <span className="news-caption">
                        <strong>{story.name}</strong>
                        <Icon name="arrow" size={16} />
                    </span>
                </button>;
            })}
        </div>
        {error && <Notice error={!isDemo}>{error}</Notice>}
    </section>;
}
export function ConnectionDetails() {
    const { accounts, active } = useCabinet();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const point = accounts.find(p => String(p.pin) === params.get('pin')) || active;
    const selected = connectionsFor(point).find(c => c.id === params.get('connection'));
    const account = selected?.account || location.state?.account;
    if (!account)
        return <>
            <PageHeading title="Подключение не выбрано" />
            <Link className="button primary" to={`/v2/pin?pin=${point.pin}`}>Выбрать подключение</Link>
        </>;
    return <>
        <PageHeading
            eyebrow={selected?.address || location.state?.account_name}
            title={account.tarif?.name || 'Подключение'}
        >
            <span className={`badge ${account.disable ? 'paused' : ''}`}>
                <span className="status-dot" />
                {account.disable ? 'Неактивно' : 'Активно'}
            </span>
        </PageHeading>
        <div className="detail-grid">
            <section className="panel">
                <h2>О подключении</h2>
                <DetailRow label="PIN">№ {point.pin}</DetailRow>
                <DetailRow label="IP-адрес">{account.IP}</DetailRow>
                <DetailRow label="Скорость по тарифу">{speedLabel(account.speed_lim)}</DetailRow>
                <DetailRow label="Текущий лимит скорости">{speedLabel(account.speed_lim_actual)}</DetailRow>
                <DetailRow label="Абонентская плата">{money(account.tarif?.abon)} ₽ / мес.</DetailRow>
            </section>
            <section className="panel">
                <h2>Трафик за месяц</h2>
                <div className="traffic-stat">
                    <span className="icon-tile blue">
                        <Icon name="download" />
                    </span>
                    <div>
                        <span className="muted">Получено</span>
                        <strong>{trafficLabel(account.trcvd)}</strong>
                    </div>
                </div>
                <div className="traffic-stat">
                    <span className="icon-tile">
                        <Icon name="upload" />
                    </span>
                    <div>
                        <span className="muted">Отправлено</span>
                        <strong>{trafficLabel(account.tsent)}</strong>
                    </div>
                </div>
                <p className="muted">Объём данных по этому подключению.</p>
            </section>
        </div>
        <div className="section-heading">
            <h2>Подключённые услуги</h2>
        </div>
        <section className="panel">
            {Object.keys(account.services || {}).length ? Object.entries(account.services).map(([id, service]) => <div className="service-row" key={id}>
                <span className="icon-tile blue">
                    <Icon name="globe" />
                </span>
                <div>
                    <strong>{service.name}</strong>
                    <p className="muted">{id === '15' ? speedLabel(service.param) : service.param}</p>
                </div>
                <strong>{money(service.on_cost)} ₽</strong>
            </div>) : <Empty title="Дополнительных услуг нет">Уточнить доступные услуги можно в поддержке.</Empty>}
        </section>
    </>;
}
export function History() {
    const { accounts } = useCabinet();
    const location = useLocation();
    const pin = new URLSearchParams(location.search).get('pin');
    const point = accounts.find(item => String(item.pin) === pin);
    return <>
        <PageHeading title="История платежей" />
        {point ? <TransactionsPanel pin={point.pin} /> : <Empty icon="history" title="Откройте нужный аккаунт">Последние платежи находятся внутри каждого PIN.</Empty>}
    </>;
}
export function Login() {
    const { associated, setAssociated, reload, isDemo, accounts } = useCabinet();
    const navigate = useNavigate();
    const location = useLocation();
    const [pin, setPin] = useState(new URLSearchParams(location.search).get('pin') || location.state?.idValue || '');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const browserOnly = !Config.tgWindow.initData && !isDemo;
    const submit = async (event, linked = false) => {
        event.preventDefault();
        setError('');
        if (isDemo) {
            setError('Добавление счетов недоступно в деморежиме. Вернитесь на главную, чтобы посмотреть пример.');
            return;
        }
        if (browserOnly)
            return;
        setBusy(true);
        try {
            let data;
            if (linked)
                data = await getApiRequest('/link');
            else {
                const response = await postApiRequest('', {}, { pin: Number(pin), password });
                data = await response.json();
                if (!response.ok)
                    throw new Error(String(data.error || data.detail || 'Не удалось войти. Проверьте PIN и пароль.'));
            }
            if (data.error || data.detail)
                throw new Error(String(data.error || data.detail));
            const points = await reload();
            if (!points.length)
                throw new Error('Счёт пока недоступен. Попробуйте войти ещё раз.');
            setAssociated(null);
            Config.HapticFeedback.success();
            navigate(urls.UserPage);
        }
        catch (err) {
            setError(err.message);
            Config.HapticFeedback.error();
        }
        finally {
            setBusy(false);
        }
    };
    return <div className="auth-layout">
        <section className="panel auth-panel">
            <h2>{accounts.length ? 'Добавить аккаунт' : 'Вход в личный кабинет'}</h2>
            <p className="muted">Введите PIN и пароль вашего договора.</p>
            {browserOnly && <Notice>Для доступа к своему счёту откройте приложение из Telegram. В браузере доступен просмотр демоверсии.</Notice>}
            {error && <Notice error>{error}</Notice>}
            {associated && <div className="associated-login">
                <span className="associated-caption">Доступен быстрый вход</span>
                <Button className="associated-button" disabled={busy || browserOnly} onClick={event => submit(event, true)}>
                    <Icon name="shield" size={24} />
                    <span>Войти с PIN {associated}</span>
                    <Icon name="arrow" size={20} />
                </Button>
                <div className="login-divider">
                    <span>или используйте другой PIN</span>
                </div>
            </div>}
            <form onSubmit={submit}>
                <label htmlFor="pin">PIN</label>
                <input
                    id="pin"
                    inputMode="numeric"
                    pattern="[0-9]+"
                    autoComplete="username"
                    placeholder="Например, 104287"
                    required
                    value={pin}
                    onChange={event => setPin(event.target.value.replace(/\D/g, ''))}
                />
                <label htmlFor="password">Пароль</label>
                <div className="password-field">
                    <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Пароль от личного кабинета"
                        required
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                    />
                    <button
                        type="button"
                        className="icon-button"
                        aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                        onClick={() => setShowPassword(value => !value)}
                    >
                        <Icon name="eye" size={20} />
                    </button>
                </div>
                <Button className="full-width" disabled={busy || browserOnly || !pin || !password}>
                    {busy ? 'Входим…' : 'Войти'}
                    <Icon name="arrow" size={18} />
                </Button>
            </form>
            <Link className="text-link" to={urls.Support}>Не помню PIN или пароль</Link>
            {browserOnly && <a className="button secondary full-width" href="/v2/user?demo=1">Посмотреть демоверсию</a>}
        </section>
    </div>;
}
export function Payment() {
    const { accounts, active, isDemo, reload } = useCabinet();
    const location = useLocation();
    const requestedPin = new URLSearchParams(location.search).get('pin') || location.state?.point?.pin;
    const point = accounts.find(item => String(item.pin) === String(requestedPin)) || active;
    const [amount, setAmount] = useState(String(location.state?.sumValue || 500));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [paymentUrl, setPaymentUrl] = useState('');
    const [status, setStatus] = useState('');
    const sum = Number(amount.replace(',', '.'));
    const valid = Number.isFinite(sum) && sum >= 10 && sum <= 10000 && /^\d+(?:[.,]\d{1,2})?$/.test(amount);
    const submit = async (event) => {
        event.preventDefault();
        setError('');
        if (!valid) {
            setError('Введите сумму от 10 до 10 000 ₽, не более двух знаков после запятой.');
            return;
        }
        if (paymentUrl) {
            Config.tgWindow.openLink(paymentUrl);
            return;
        }
        if (isDemo) {
            setStatus('Это демоверсия. Реальный платёж не создан, баланс не изменён.');
            return;
        }
        setBusy(true);
        try {
            const data = await getApiRequest('/pay', { pin: point.pin, sum });
            if (data.error || data.detail)
                throw new Error(String(data.error || data.detail));
            const url = new URL(data.text);
            if (url.protocol !== 'https:')
                throw new Error('Сервис вернул некорректную платёжную ссылку.');
            setPaymentUrl(url.href);
            Config.tgWindow.openLink(url.href);
            setStatus('Платёжная страница открыта. После оплаты обновите баланс — зачисление может занять некоторое время.');
        }
        catch (err) {
            setError(err.message || 'Не удалось создать платёж. Попробуйте позже.');
        }
        finally {
            setBusy(false);
        }
    };
    if (point.error)
        return <AccountError />;
    return <>
        <PageHeading eyebrow="БЕЗНАЛИЧНАЯ ОПЛАТА" title="Пополнить баланс" />
        <div className="payment-layout">
            <section className="panel payment-form">
                <span className="icon-tile blue">
                    <Icon name="card" size={27} />
                </span>
                <h2>Сколько зачислить?</h2>
                <p className="muted">PIN {point.pin}</p>
                <form onSubmit={submit}>
                    <label htmlFor="amount">Сумма пополнения</label>
                    <div className="amount-field">
                        <input
                            id="amount"
                            inputMode="decimal"
                            value={amount}
                            onChange={event => { setAmount(event.target.value); setError(''); setPaymentUrl(''); setStatus(''); }}
                            aria-describedby="amount-help"
                        />
                        <span>₽</span>
                    </div>
                    <p id="amount-help" className="muted small">От 10 до 10 000 ₽</p>
                    <div className="amount-presets">
                        {[500, 750, 1000, 1500].map(value => <button
                            type="button"
                            key={value}
                            className={sum === value ? 'selected' : ''}
                            onClick={() => { setAmount(String(value)); setPaymentUrl(''); setStatus(''); setError(''); }}
                        >{money(value)} ₽</button>)}
                    </div>
                    <div className="payment-method">
                        <span className="sbp-mark">
                            <i />
                            <i />
                            <i />
                        </span>
                        <div>
                            <strong>Система быстрых платежей</strong>
                            <p className="muted small">Оплата в приложении вашего банка</p>
                        </div>
                        <span className="method-check">
                            <Icon name="check" size={15} />
                        </span>
                    </div>
                    {error && <Notice error>{error}</Notice>}
                    {status && <Notice>{status}</Notice>}
                    <Button className="full-width" disabled={busy || !valid}>
                        {busy ? 'Создаём платёж…' : isDemo ? 'Посмотреть пример оплаты' : `Перейти к оплате ${money(sum)} ₽`}
                        <Icon name="arrow" size={19} />
                    </Button>
                </form>
                <div className="payment-followup">
                    {status && <Button
                        variant="secondary"
                        onClick={async () => {
                            try {
                                await reload();
                                setStatus('Баланс обновлён по данным сервера.');
                            }
                            catch (err) {
                                setError(err.message);
                            }
                        }}
                    >Обновить баланс</Button>}
                </div>
            </section>
            <section className="panel payment-summary">
                <h2>Детали пополнения</h2>
                <DetailRow label="PIN">№ {point.pin}</DetailRow>
                <DetailRow label="Абонент">{point.name}</DetailRow>
                <DetailRow label="Текущий баланс">{money(point.balance)} ₽</DetailRow>
                <DetailRow label="Сумма">{valid ? money(sum) : '—'} ₽</DetailRow>
                <div className="payment-total">
                    <span className="muted">Баланс после зачисления</span>
                    <strong>
                        {valid && point.balance != null ? money(Number(point.balance) + sum) : '—'} <span>₽</span>
                    </strong>
                </div>
                <p className="security-note"><Icon name="shield" size={18} /> Данные карты вводятся на стороне банка. CLN не запрашивает их в приложении.</p>
            </section>
        </div>
    </>;
}
export function Settings() {
    const { accounts, active: selected, isDemo, logout, reload } = useCabinet();
    const location = useLocation();
    const pin = new URLSearchParams(location.search).get("pin");
    const active = accounts.find(point => String(point.pin) === pin) || selected;
    const [settings, setSettings] = useState(null);
    const [enabled, setEnabled] = useState(!!active.telegram_id);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [confirm, setConfirm] = useState(null);
    const navigate = useNavigate();
    useEffect(() => {
        let cancelled = false;
        setSettings(null);
        setError('');
        setEnabled(!!active.telegram_id);
        setConfirm(null);
        if (isDemo) {
            setSettings({ clients: [{ telegram_id: 123, last_login_os: 'Telegram · iPhone', last_login: '2026-10-16', is_current_user: true }] });
            return;
        }
        postApiRequest('/settings', {}, { getSettings: { pin: active.pin } }).then(async (response) => {
            const data = await response.json();
            if (!response.ok || data.error || data.detail)
                throw new Error(String(data.error || data.detail || 'Не удалось загрузить настройки.'));
            if (!cancelled)
                setSettings(data);
        }).catch(err => {
            if (!cancelled)
                setError(err.message);
        });
        return () => { cancelled = true; };
    }, [active.pin, active.telegram_id, isDemo]);
    const toggle = async () => {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            if (!isDemo) {
                const response = await postApiRequest('/settings', {}, { setNotifications: { pin: active.pin, value: !enabled } });
                const data = await response.json();
                if (!response.ok || data.error || data.detail)
                    throw new Error(String(data.error || data.detail || 'Не удалось изменить настройку.'));
            }
            setEnabled(!enabled);
            setMessage(isDemo ? 'Демонстрационная настройка изменена только на этом экране.' : 'Настройка уведомлений сохранена.');
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setBusy(false);
        }
    };
    const unlink = async () => {
        setBusy(true);
        setError('');
        try {
            await logout(active.pin, confirm.telegram_id);
            if (confirm.telegram_id)
                setSettings(value => ({ ...value, clients: Object.values(value.clients || {}).filter(client => client.telegram_id !== confirm.telegram_id) }));
            else
                navigate(urls.UserPage);
            setConfirm(null);
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setBusy(false);
        }
    };
    return <>
        <PageHeading eyebrow="УПРАВЛЕНИЕ ДОСТУПОМ" title="Настройки счёта" />
        <p className="settings-pin">PIN {active.pin}</p>
        {error && <Notice error>{error}</Notice>}
        {message && <Notice>{message}</Notice>}
        <div className="detail-grid">
            <section className="panel">
                <h2>Уведомления</h2>
                <div className="notification-row">
                    <span className="icon-tile blue">
                        <Icon name="bell" />
                    </span>
                    <div>
                        <strong>Уведомления о платежах</strong>
                        <p className="muted small">Получать сообщения в Telegram</p>
                    </div>
                    <button
                        className={`switch ${enabled ? 'on' : ''}`}
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        aria-label="Уведомления о платежах"
                        disabled={busy || active.error}
                        onClick={toggle}
                    >
                        <span />
                    </button>
                </div>
                <p className="muted small">Уведомления можно подключить только к одному Telegram-аккаунту.</p>
                {active.telegram_id && <p className="muted small">
                    {String(active.telegram_id) === String(Config.user.id) ? 'Подключены к вашему аккаунту.' : `Текущий получатель: Telegram ID ${active.telegram_id}`}
                </p>}
            </section>
            <section className="panel">
                <h2>Подключённые устройства</h2>
                {!settings ? <p className="muted">{error ? 'Информация недоступна.' : 'Загружаем устройства…'}</p> : Object.values(settings.clients || {}).length ? Object.values(settings.clients || {}).map((client, index) => <div className="device-row" key={client.telegram_id || index}>
                    <span className="icon-tile">
                        <Icon name="user" />
                    </span>
                    <div>
                        <strong>{client.last_login_os || 'Telegram'}</strong>
                        <p className="muted small">{dateLabel(client.last_login)}</p>
                    </div>
                    {client.is_current_user ? <span className="badge">Вы</span> : <button
                        className="icon-button danger"
                        aria-label={`Отключить ${client.last_login_os || 'устройство'}`}
                        onClick={() => setConfirm(client)}
                    >
                        <Icon name="logout" />
                    </button>}
                </div>) : <p className="muted">Устройства не найдены.</p>}
            </section>
        </div>
        <section className="panel logout-panel">
            <div>
                <h3>Отключить PIN</h3>
                <p className="muted">PIN {active.pin} и его подключения будут удалены из этого кабинета. Договор останется действующим.</p>
            </div>
            <Button variant="danger" onClick={() => setConfirm({})}>Отключить PIN</Button>
        </section>
        {confirm && <section className="panel confirm-panel" role="alert">
            <h3>{confirm.telegram_id ? 'Отключить это устройство?' : `Отключить PIN ${active.pin}?`}</h3>
            <p className="muted">Для повторного подключения потребуется PIN и пароль. Если устройство вам неизвестно, смените пароль в личном кабинете провайдера.</p>
            <div className="button-row">
                <Button variant="danger" disabled={busy} onClick={unlink}>{busy ? 'Отключаем…' : 'Да, отключить'}</Button>
                <Button variant="secondary" disabled={busy} onClick={() => setConfirm(null)}>Отмена</Button>
            </div>
        </section>}
        <button className="text-button" onClick={() => reload().catch(err => setError(err.message))}>Обновить данные счёта</button>
    </>;
}
export function Support() {
    return <>
        <PageHeading eyebrow="ПОДДЕРЖКА CLN" title="Мы на связи" />
        <div className="detail-grid">
            <section className="panel support-main">
                <span className="icon-tile blue">
                    <Icon name="help" size={30} />
                </span>
                <h2>Поможем с вашим интернетом</h2>
                <p className="muted">Вопросы об оплате, подключении и услугах — позвоните нам. При обращении подготовьте PIN.</p>
                <a className="support-phone" href="tel:+74956405700">+7 (495) 640-57-00</a>
            </section>
            <section className="panel">
                <h2>Частые вопросы</h2>
                {[
                    ['Как пополнить баланс?', 'На главной найдите нужный PIN и нажмите «Пополнить баланс». Введите сумму от 10 до 10 000 рублей и перейдите к оплате через СБП.'],
                    ['Где найти PIN и пароль?', 'Используйте данные из договора или личного кабинета провайдера. Если не можете их найти, позвоните в поддержку CLN.'],
                    ['Как добавить ещё один адрес?', 'Если адрес относится к вашему PIN, он появится в списке подключений этого счёта. Для другого PIN нажмите «Добавить PIN» на главной.'],
                    ['Что делать, если интернет не работает?', 'Проверьте баланс и статус подключения. Перезагрузите роутер и дождитесь восстановления соединения. Если это не помогло, обратитесь в поддержку.'],
                    ['Оплатил, но баланс не изменился', 'Вернитесь на главную и нажмите «Обновить». Зачисление может занять некоторое время. Если платёж долго не отображается, обратитесь в поддержку с чеком.'],
                ].map(([question, answer]) => <details className="faq" key={question}>
                    <summary>
                        {question}
                        <Icon name="plus" size={18} />
                    </summary>
                    <p>{answer}</p>
                </details>)}
            </section>
        </div>
    </>;
}